import { BrandStyles } from '@/components/settings/Website/BrandStyles.tsx';
import { ContentTab } from '@/components/settings/Website/ContentTab.tsx';
import { CustomCode } from '@/components/settings/Website/CustomCode.tsx';
import { DomainsTab } from '@/components/settings/Website/DomainsTab.tsx';
import { LinksTab } from '@/components/settings/Website/LinksTab.tsx';
import { SearchAndSocial } from '@/components/settings/Website/SearchAndSocial.tsx';
import { AccountContentContextProvider } from '@/contexts/AccountContentContext';
import { useDashboardContext } from '@/contexts/DashboardContext';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@superfunnel/ui-core/v2/Tabs';
import { Code, DesignNib, Globe, Link, Search, WrapText } from 'iconoir-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function WebsiteSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isNewAccount } = useDashboardContext();

  const [selectedSubTab, setSelectedSubTab] = useState<string>(
    searchParams.get('tab') || 'links',
  );

  useEffect(() => {
    setSearchParams((prev) => {
      prev.set('tab', selectedSubTab);
      return new URLSearchParams(prev);
    });
  }, [selectedSubTab, setSearchParams]);

  const subTabs = useMemo(
    () =>
      [
        {
          id: 'links',
          icon: <Link className={'text-base-500 size-5'} />,
          name: 'Links',
          current: false,
        },
        ...(isNewAccount
          ? []
          : [
              {
                id: 'content',
                icon: <WrapText className={'text-base-500 size-5'} />,
                name: 'Content',
                current: false,
              },
            ]),
        {
          id: 'domains',
          icon: <Globe className={'text-base-500 size-5'} />,
          name: 'Domains',
          current: false,
        },
        {
          id: 'brand-styles',
          icon: <DesignNib className={'text-base-500 size-5'} />,
          name: 'Brand Styles',
          current: false,
        },
        {
          id: 'search-and-social',
          icon: <Search className={'text-base-500 size-5'} />,
          name: 'Search & Social',
          current: false,
        },
        {
          id: 'custom-code',
          icon: <Code className={'text-base-500 size-5'} />,
          name: 'Custom Code',
          current: false,
        },
      ].map((c) => {
        c.current = c.id === selectedSubTab;
        return c;
      }),
    [isNewAccount, selectedSubTab],
  );

  return (
    <Tabs
      defaultValue={selectedSubTab}
      onValueChange={setSelectedSubTab}
      orientation={'vertical'}
      className="grid h-full w-full grid-cols-1 gap-6 overflow-hidden bg-transparent md:grid-cols-[200px_1fr]"
    >
      <TabsList
        className={
          'h-min w-full flex-wrap items-start justify-start p-0 md:sticky md:top-0 md:flex-col'
        }
      >
        {subTabs.map((tab) => (
          <TabsTrigger
            value={tab.id}
            key={tab.id}
            className={
              'data-[state=active]:bg-base-100 h-auto flex-none justify-start gap-2.5 p-2 data-[state=active]:shadow-none md:w-full'
            }
          >
            {tab.icon} {tab.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent
        value="links"
        className={'grid h-full grid-rows-[1fr_auto] overflow-hidden'}
      >
        <LinksTab />
      </TabsContent>
      <TabsContent value="content" className={'h-full overflow-y-auto'}>
        <AccountContentContextProvider>
          <ContentTab />
        </AccountContentContextProvider>
      </TabsContent>
      <TabsContent value="domains" className={'h-full overflow-y-auto'}>
        <DomainsTab />
      </TabsContent>
      <TabsContent
        value="brand-styles"
        className={'grid h-full grid-rows-[1fr_auto] overflow-hidden'}
      >
        <BrandStyles />
      </TabsContent>
      <TabsContent
        value="search-and-social"
        className={'h-full overflow-y-auto'}
      >
        <SearchAndSocial />
      </TabsContent>
      <TabsContent
        value="custom-code"
        className={'grid h-full grid-rows-[1fr_auto] overflow-hidden'}
      >
        <CustomCode />
      </TabsContent>
    </Tabs>
  );
}
